import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { getConsent, saveConsent } from "@/consent/consentManager";

const EASE = [0.22, 1, 0.36, 1];

const Toggle = ({ checked, onChange, disabled, label, testid }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF] ${
            disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
        } ${checked ? "bg-gradient-to-r from-[#00E5FF] to-[#A855F7]" : "bg-white/15"}`}
    >
        <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-300 ${
                checked ? "translate-x-6" : "translate-x-1"
            }`}
        />
    </button>
);

export default function CookieConsent() {
    const [bannerOpen, setBannerOpen] = useState(false);
    const [prefsOpen, setPrefsOpen] = useState(false);
    const [analyticsOn, setAnalyticsOn] = useState(false);

    useEffect(() => {
        setBannerOpen(!getConsent());
        const openSettings = () => {
            setAnalyticsOn(getConsent()?.analytics ?? false);
            setBannerOpen(false);
            setPrefsOpen(true);
        };
        window.addEventListener("tedops:open-cookie-settings", openSettings);
        return () => window.removeEventListener("tedops:open-cookie-settings", openSettings);
    }, []);

    const choose = useCallback((analytics, action) => {
        saveConsent(analytics, action);
        setBannerOpen(false);
        setPrefsOpen(false);
    }, []);

    const openPreferences = () => {
        setAnalyticsOn(getConsent()?.analytics ?? false);
        setPrefsOpen(true);
    };

    const closePreferences = () => {
        setPrefsOpen(false);
        if (!getConsent()) setBannerOpen(true);
    };

    return (
        <>
            <AnimatePresence>
                {bannerOpen && !prefsOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 24 }}
                        transition={{ duration: 0.4, ease: EASE }}
                        className="fixed inset-x-4 bottom-4 z-[100] sm:inset-x-6 sm:bottom-6"
                        data-testid="cookie-banner"
                    >
                        <div
                            role="region"
                            aria-label="Cookie consent"
                            className="mx-auto max-w-3xl rounded-2xl border border-white/10 bg-[#0A0F1C] p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] sm:p-7"
                        >
                            <div className="flex items-start gap-4">
                                <span className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0066FF]/15 sm:flex">
                                    <Cookie className="h-5 w-5 text-[#22D3EE]" strokeWidth={1.75} aria-hidden="true" />
                                </span>
                                <div>
                                    <p className="font-display text-base font-bold text-white">We value your privacy</p>
                                    <p className="mt-1.5 text-sm leading-relaxed text-[#94A3B8]">
                                        We use strictly necessary storage to make this site work. With your consent we
                                        also use analytics cookies to understand how the site is used. You can accept,
                                        reject or customise optional cookies — and change your mind at any time via{" "}
                                        <span className="text-white/80">Cookie Settings</span> in the footer. See our{" "}
                                        <Link
                                            to="/cookie-policy/"
                                            className="text-[#22D3EE] underline decoration-[#22D3EE]/40 underline-offset-2 transition-colors hover:decoration-[#22D3EE]"
                                            data-testid="cookie-banner-policy-link"
                                        >
                                            Cookie Policy
                                        </Link>
                                        .
                                    </p>
                                </div>
                            </div>
                            <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                                <button
                                    type="button"
                                    onClick={() => choose(true, "accept_all")}
                                    className="min-h-[44px] flex-1 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]"
                                    data-testid="cookie-accept-all"
                                    autoFocus
                                >
                                    Accept All
                                </button>
                                <button
                                    type="button"
                                    onClick={() => choose(false, "reject_optional")}
                                    className="min-h-[44px] flex-1 rounded-full border border-white/20 bg-white/10 px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]"
                                    data-testid="cookie-reject-optional"
                                >
                                    Reject Optional
                                </button>
                                <button
                                    type="button"
                                    onClick={openPreferences}
                                    className="min-h-[44px] rounded-full px-4 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#94A3B8] underline decoration-white/20 underline-offset-4 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]"
                                    data-testid="cookie-manage-preferences"
                                >
                                    Manage Preferences
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {prefsOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="fixed inset-0 z-[110] flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
                        onClick={closePreferences}
                        data-testid="cookie-preferences-overlay"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 24 }}
                            transition={{ duration: 0.35, ease: EASE }}
                            role="dialog"
                            aria-modal="true"
                            aria-label="Cookie preferences"
                            className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0A0F1C] p-6 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.7)] sm:p-7"
                            onClick={(e) => e.stopPropagation()}
                            data-testid="cookie-preferences-modal"
                        >
                            <div className="flex items-center justify-between">
                                <p className="font-display text-lg font-bold text-white">Cookie Preferences</p>
                                <button
                                    type="button"
                                    onClick={closePreferences}
                                    aria-label="Close cookie preferences"
                                    className="flex h-11 w-11 items-center justify-center rounded-full text-[#94A3B8] transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]"
                                    data-testid="cookie-preferences-close"
                                >
                                    <X className="h-5 w-5" strokeWidth={2} />
                                </button>
                            </div>

                            <div className="mt-5 space-y-4">
                                <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-white/[0.03] p-4">
                                    <div>
                                        <p className="text-sm font-bold text-white">Strictly necessary</p>
                                        <p className="mt-1 text-xs leading-relaxed text-[#94A3B8]">
                                            Required for security and core site operation. Always active.
                                        </p>
                                    </div>
                                    <Toggle checked disabled label="Strictly necessary cookies (always on)" testid="cookie-necessary-toggle" onChange={() => {}} />
                                </div>
                                <div className="flex items-center justify-between gap-4 rounded-xl border border-white/[0.07] bg-white/[0.03] p-4">
                                    <div>
                                        <p className="text-sm font-bold text-white">Analytics</p>
                                        <p className="mt-1 text-xs leading-relaxed text-[#94A3B8]">
                                            Helps us understand site usage (PostHog). Off means no analytics cookies,
                                            storage or collection.
                                        </p>
                                    </div>
                                    <Toggle
                                        checked={analyticsOn}
                                        onChange={setAnalyticsOn}
                                        label="Analytics cookies"
                                        testid="cookie-analytics-toggle"
                                    />
                                </div>
                            </div>

                            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                <button
                                    type="button"
                                    onClick={() => choose(analyticsOn, "save_preferences")}
                                    className="min-h-[44px] flex-1 rounded-full bg-gradient-to-r from-[#00E5FF] via-[#0066FF] to-[#A855F7] px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-transform duration-300 hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]"
                                    data-testid="cookie-save-preferences"
                                >
                                    Save Preferences
                                </button>
                                <button
                                    type="button"
                                    onClick={() => choose(false, "reject_optional")}
                                    className="min-h-[44px] flex-1 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#00E5FF]"
                                    data-testid="cookie-preferences-reject"
                                >
                                    Reject Optional
                                </button>
                            </div>
                            <p className="mt-4 text-center text-[11px] leading-relaxed text-[#64748B]">
                                Withdrawing consent stops all optional tracking immediately and clears optional cookies
                                and storage. Read our{" "}
                                <Link
                                    to="/cookie-policy/"
                                    className="text-[#22D3EE] underline decoration-[#22D3EE]/40 underline-offset-2"
                                    onClick={() => setPrefsOpen(false)}
                                    data-testid="cookie-preferences-policy-link"
                                >
                                    Cookie Policy
                                </Link>
                                .
                            </p>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
